import { Component, computed, DestroyRef, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { ProductsFileMgt } from '../../../../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';
import { MfgExpDatesIcon } from '../../../../../../shared/ui/icons/product-card/mfg-exp-dates-icon/mfg-exp-dates-icon';
import { BatchNumberIcon } from '../../../../../../shared/ui/icons/product-card/batch-number-icon/batch-number-icon';
import { Loading } from '../../../../../../utils/loading/loading';
import { CustomerPriceIcon } from '../../../../../../shared/ui/icons/product-card/customer-price-icon/customer-price-icon';
import { BusinessPriceIcon } from '../../../../../../shared/ui/icons/product-card/business-price-icon/business-price-icon';
import { ItemQuantityStatus } from '../item-quantity-status/item-quantity-status';
import { SubmitButton } from '../../../../../../shared/ui/Buttons/submit-button/submit-button';

@Component({
  selector: 'app-order-offer-item',
  standalone: true,
  imports: [
    MfgExpDatesIcon,
    BatchNumberIcon,
    Loading,
    CustomerPriceIcon,
    BusinessPriceIcon,
    ItemQuantityStatus,
    SubmitButton,
  ],
  templateUrl: './order-offer-item.html',
  styleUrl: './order-offer-item.scss',
})
export class OrderOfferItem {
  private fileMgt = inject(ProductsFileMgt);
  private destroyRef = inject(DestroyRef);
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));
  productTitle = input();

  totalBusinessPrice = input();
  quantity = input();

  profileImageFid = input<string>();
  isLoadingFile = signal(false);
  previewImageUrl = signal<SafeUrl | string>('/assets/product.png');

  buyQuantity = input();
  getQuantity = input();
  products = input<any[]>();

  isPrinted = input<boolean>(false);
  packageState = input<Record<string, { packageQuantity: string | null; notPackageRows: any[] }>>({});
  offerItemId = input<string | number>();

  packageClicked = output<any>();

  isPartiallyPackaged = input<boolean>(false);
  offerNotPackage = input<any[] | null>(null);
  packageQuantity = input<string | null>(null);
  notPackageRows = input<any[] | null>(null);
  confirmAction = input<string | null>(null);

  convertClicked = output<void>();
  dropAllClicked = output<void>();

  isReadOnly = input<boolean>(false);
  receiveReturnedProductsView = input<boolean>(false);

  showOfferShortageActions = computed(() => {
    if (this.isReadOnly()) return false;
    if (this.confirmAction()) return false;
    return this.isPartiallyPackaged() && this.offerNotPackageRows().length > 0;
  });

  offerNotPackageRows = computed<any[]>(() => {
    const rows = (this.products() ?? []).flatMap((row: any) => this.getPackageDataFor(row).notPackageRows ?? []);
    if (rows.length > 0) return rows;
    if ((this.notPackageRows()?.length ?? 0) > 0) return this.notPackageRows()!;
    return this.offerNotPackage() ?? [];
  });

  isOfferFullyUnpackaged = computed(() => {
    const ordered = +String(this.quantity());
    if (!ordered) return false;
    const rows = this.offerNotPackageRows();
    if (rows.length === 0) return false;
    const notPacked = rows.reduce((acc, r) => acc + (+r.quantity || 0), 0);
    return notPacked >= ordered;
  });

  onConvertClick() {
    this.convertClicked.emit();
  }

  onDropAllClick() {
    this.dropAllClicked.emit();
  }

  totalCustomerPrice = computed(() =>
    this.products()?.reduce((sum, product) => sum + product.product.peoplePrice * product.quantity, 0),
  );

  totalPharmacyPrice = computed(() =>
    this.products()?.reduce((sum, product) => sum + product.product.storePrice * product.quantity, 0),
  );

  getPackageDataFor(row: any) {
    const storeEntry = this.getStoreEntryFor(row);
    if (storeEntry) {
      return {
        packageQuantity: storeEntry.packageQuantity ?? null,
        notPackageRows: storeEntry.notPackageRows ?? [],
      };
    }

    const product = row?.product ?? row;
    const history = row?.packagingHistory ?? product?.packagingHistory;
    const hasHistory = history && (history.packaged !== undefined || history.notPackaged !== undefined);
    const packaged = hasHistory ? history.packaged : product?.package;
    const notPackage = hasHistory ? history.notPackaged : product?.notPackage;

    const packageQuantity = packaged?.quantity != null ? String(packaged.quantity) : null;

    const notPackageRows = Array.isArray(notPackage)
      ? notPackage.map((r: any) => {
          const reasonWrapper = r.reason != null ? { reason: r.reason, otherReason: r.otherReason } : r.productReason ?? null;
          const reason = reasonWrapper?.reason ?? r;
          return {
            quantity: r?.quantity != null ? String(r.quantity) : '0',
            reasonId: reason?.id,
            reasonTitleEn: reason?.titleEn,
            reasonTitleAr: reason?.titleAr,
            otherReason: reasonWrapper?.otherReason ?? undefined,
          };
        })
      : [];

    return { packageQuantity, notPackageRows };
  }

  private getStoreEntryFor(row: any): { packageQuantity: string | null; notPackageRows: any[] } | undefined {
    const state = this.packageState();
    if (!state) return undefined;

    const rowId = row?.id;
    if (rowId == null) return undefined;

    if (this.offerItemId() != null) {
      const scoped = state[`${this.offerItemId()}_${rowId}`];
      if (scoped) return scoped;
    }

    return state[rowId];
  }

  concatenationDate(mfgDate: string, expDate: string) {
    const date = new Date(expDate);
    const shortYear = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const expDateYear = month + ' - ' + shortYear;

    const mfgDateVar = new Date(mfgDate);
    const mfgDateYear = mfgDateVar.getFullYear().toString().slice(-2);

    return mfgDateYear + ' - ' + expDateYear;
  }

  onOpenPackageModal(row: any) {
    if (this.isReadOnly()) return;
    this.packageClicked.emit(row);
  }

  ngOnChanges() {
    const fid = this.profileImageFid();
    if (!fid) return;

    this.isLoadingFile.set(true);

    const subscription = this.fileMgt.getImageUrl(fid).subscribe({
      next: (res) => {
        this.isLoadingFile.set(false);

        if (res.isImage) {
          this.previewImageUrl.set(res.url);
        } else {
          window.open(res.url as string, '_blank');
        }
      },
      error: () => {
        this.isLoadingFile.set(false);
        this.previewImageUrl.set('/assets/product.png');
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }
}
